import type { FC, FormEvent, ReactNode } from 'react'
import type { AuthErrorResponse } from '@/types'

import { useEffect, useState } from 'react'
import { LoaderCircle, MailCheck } from 'lucide-react'
import { validateEmail } from '@/lib/auth-validation'
import { Seo } from '@/components/generals/seo'
import { Button } from '@/components/ui/button'
import AuthLayout from '@/components/auth/auth-layout'
import { BackToLogin, recoveryFeatures } from '@/components/auth/recovery'

import {
    AuthDivider,
    AuthHeading,
    FormAlert,
    TextField
} from '@/components/auth/form-controls'

type ForgotResponse = { ok: true } | AuthErrorResponse

const RESEND_SECONDS = 60

const LupaKataSandi: FC = (): ReactNode => {
    const [email, setEmail] = useState('')
    const [error, setError] = useState<string | undefined>()
    const [alert, setAlert] = useState<{ title: string, message: string } | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const [sentTo, setSentTo] = useState<string | null>(null)
    const [cooldown, setCooldown] = useState(0)

    useEffect(() => {
        if (cooldown <= 0) return

        const timer = window.setTimeout(() => setCooldown(value => value - 1), 1000)

        return () => window.clearTimeout(timer)
    }, [cooldown])

    const requestLink = async (address: string): Promise<boolean> => {
        setSubmitting(true)
        setAlert(null)

        try {
            const response = await fetch('/api/auth/lupa-kata-sandi', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: address })
            })

            const data = await response.json() as ForgotResponse

            if (data.ok) {
                setCooldown(RESEND_SECONDS)
                return true
            }

            setError(data.fields?.email)
            setAlert({ title: data.title, message: data.message })
            return false
        }
        catch {
            setAlert({ title: 'Koneksi terputus', message: 'Periksa koneksi internet Anda, lalu coba lagi.' })
            return false
        }
        finally {
            setSubmitting(false)
        }
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const emailError = validateEmail(email)

        if (emailError) {
            setError(emailError)
            setAlert(null)
            document.getElementById('lupa-email')?.focus()
            return
        }

        const sent = await requestLink(email.trim())

        if (sent) {
            setSentTo(email.trim())
            window.scrollTo({ top: 0 })
        }
    }

    return (
        <AuthLayout
            title='Kembali mengakses layanan klinik.'
            description='Atur ulang kata sandi melalui email yang digunakan saat mendaftar akun.'
            features={recoveryFeatures}
        >
            <Seo title='Lupa kata sandi' />

            {sentTo ? (
                <div>
                    <span className='flex size-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-700'>
                        <MailCheck className='size-6' aria-hidden='true' />
                    </span>

                    <div className='mt-6'>
                        <AuthHeading
                            title='Permintaan reset diterima'
                            description='Jika email tersebut terdaftar, tautan untuk mengatur ulang kata sandi sudah kami kirim ke email itu.'
                        />
                    </div>

                    <div className='mt-6 rounded-2xl bg-muted/60 px-4 py-3.5 ring-1 ring-border ring-inset'>
                        <p className='text-sm text-muted-foreground'>
                            Email yang Anda masukkan
                        </p>

                        <p className='mt-0.5 font-semibold break-all text-ink'>
                            {sentTo}
                        </p>
                    </div>

                    <FormAlert tone='info' title='Tautan berlaku 60 menit' className='mt-5'>
                        Buka email dari Klinik Sehat Budi Luhur, lalu klik tombol di dalamnya. Periksa folder spam bila email belum masuk dalam beberapa menit.
                    </FormAlert>

                    {alert && (
                        <FormAlert title={alert.title} className='mt-5'>
                            {alert.message}
                        </FormAlert>
                    )}

                    <Button
                        size='lg'
                        className='mt-6 w-full'
                        disabled={submitting || cooldown > 0}
                        onClick={() => void requestLink(sentTo)}
                    >
                        {submitting && <LoaderCircle className='size-5 animate-spin' aria-hidden='true' />}
                        {cooldown > 0 ? `Kirim ulang tautan (${cooldown} detik)` : 'Kirim ulang tautan'}
                    </Button>

                    <p className='mt-4 text-center'>
                        <button
                            type='button'
                            onClick={() => {
                                setSentTo(null)
                                setAlert(null)
                            }}
                            className='rounded-md text-[15px] font-semibold text-brand-700 outline-none hover:underline focus-visible:ring-4 focus-visible:ring-ring/30'
                        >
                            Coba email lain
                        </button>
                    </p>

                    <AuthDivider />
                    <BackToLogin />
                </div>
            ) : (
                <form onSubmit={handleSubmit} noValidate>
                    <AuthHeading
                        title='Lupa kata sandi?'
                        description='Masukkan email pemilik akun yang digunakan saat mendaftar untuk mengatur ulang kata sandi.'
                    />

                    {alert && (
                        <FormAlert title={alert.title} className='mt-6'>
                            {alert.message}
                        </FormAlert>
                    )}

                    <TextField
                        id='lupa-email'
                        label='Email'
                        type='email'
                        inputMode='email'
                        autoComplete='email'
                        placeholder='nama@contoh.com'
                        className='mt-6'
                        hint={(
                            <>
                                Gunakan email pemilik akun.
                                <span className='mt-3 block'>
                                    Untuk orang tua/wali, gunakan email pemilik akun, bukan email anak.
                                </span>
                            </>
                        )}
                        error={error}
                        value={email}
                        onChange={event => {
                            setEmail(event.target.value)
                            if (error) setError(undefined)
                        }}
                    />

                    <Button type='submit' size='lg' className='mt-6 w-full' disabled={submitting}>
                        {submitting && <LoaderCircle className='size-5 animate-spin' aria-hidden='true' />}
                        {submitting ? 'Mengirim...' : 'Kirim tautan reset'}
                    </Button>

                    <AuthDivider />
                    <BackToLogin />
                </form>
            )}
        </AuthLayout>
    )
}

export default LupaKataSandi
